import React from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { tokens } from "../../theme";

const PositionList = ({ title, players, color, highlightedPlayer }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const abbreviateName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return fullName || '';
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
      // Single token name, just return as-is
      return parts[0];
    }
    const first = parts[0];
    const last = parts[parts.length - 1];
    const firstInitial = first.charAt(0);
    return `${firstInitial}. ${last}`;
  };

  return (
    <Box
      sx={{
        backgroundColor: colors.primary[400],
        borderRadius: "4px",
        padding: "8px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: colors.grey[100],
          fontSize: { xs: "12px", sm: "12px", md: "13px" },
          fontWeight: "bold",
          mb: { xs: 0.5, sm: 0.5, md: 1 },
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: { xs: "10px", sm: "10px", md: "12px" },
            height: { xs: "10px", sm: "10px", md: "12px" },
            borderRadius: "2px",
            backgroundColor: color,
          }}
        />
        {title} ({players.length})
      </Typography>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: colors.primary[300],
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: colors.blueAccent[500],
            borderRadius: "4px",
            border: `1px solid ${colors.primary[300]}`,
            "&:hover": {
              background: colors.blueAccent[400],
            },
          },
          "&::-webkit-scrollbar-corner": {
            background: colors.primary[300],
          },
        }}
      >
        {(() => {
          // Render list with tier dividers instead of inline T#
          const items = [];
          let currentTier = null;
          const filtered = players.filter(p => p && p.name);
          for (let i = 0; i < filtered.length; i++) {
            const player = filtered[i];
            const tier = player.pos_tier;
            const showTier = tier !== undefined && tier !== null && tier !== 99;

            // If tier changed and there are players in this tier remaining, add a divider
            if (showTier && tier !== currentTier) {
              // Check if any players with this tier remain from here forward
              const hasMoreInTier = filtered.slice(i).some(p => p.pos_tier === tier);
              if (hasMoreInTier) {
                items.push(
                  <Box key={`tier-${tier}-${title}-${i}`} sx={{ my: 0.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: colors.grey[300],
                      }}
                    >
                      <Box sx={{ flex: 1, height: "1px", backgroundColor: colors.grey[600] }} />
                      <Typography sx={{ fontSize: "11px", color: colors.grey[300] }}>{`Tier ${tier}`}</Typography>
                      <Box sx={{ flex: 1, height: "1px", backgroundColor: colors.grey[600] }} />
                    </Box>
                  </Box>
                );
              }
              currentTier = tier;
            }

            items.push(
              <Box
                key={player.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: { xs: "2px 6px", sm: "2px 6px", md: "2px 8px" },
                  borderRadius: "2px",
                  mb: 0.5,
                  backgroundColor: colors.primary[500],
                  border:
                    highlightedPlayer &&
                    player.name
                      .toLowerCase()
                      .includes(highlightedPlayer.toLowerCase())
                      ? `2px solid ${colors.yellowAccent[500]}`
                      : "none",
                  height: { xs: "22px", sm: "22px", md: "24px" },
                  "&:last-child": { mb: 0 },
                }}
              >
                <Typography
                  sx={{
                    color: colors.grey[100],
                    fontSize: { xs: "11px", sm: "11px", md: "12px" },
                    fontWeight: "bold",
                    lineHeight: 1.2,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isSmall ? abbreviateName(player.name) : player.name}
                </Typography>
              </Box>
            );
          }
          return items;
        })()}
      </Box>
    </Box>
  );
};

export default PositionList;
